import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Sparkles, MessageCircle, Mic, Coffee, Users, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FallingMapleLeaves } from '@/components/shared/FallingMapleLeaves';

const features = [
  { icon: MessageCircle, title: 'Hot Takes', desc: 'Confess your spiciest carbon opinion' },
  { icon: Briefcase, title: 'Tell Us What You\'re Working On', desc: '' },
  { icon: Mic, title: 'Show & Tell', desc: '2 min — brag about your project' },
  { icon: Coffee, title: 'The Tea', desc: 'What actually broke this week' },
  { icon: Users, title: 'Open Floor', desc: 'Drinks, debate, no wrong answers' },
];

const CarbonCircularLivePage = () => {
  return (
    <main data-testid="carbon-circular-live-page" className="overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0 -top-20">
          <img src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80" alt="Canvas" className="w-full h-full object-cover" style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-green/8 via-transparent to-brand-brown/12" />
        </div>
        <FallingMapleLeaves />
        <div className="w-full pl-6 md:pl-12 lg:pl-24 pr-6 relative z-10">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <Link to="/events" className="inline-flex items-center font-sans text-sm text-gray-600 hover:text-brand-green transition-colors mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Events
            </Link>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-brown/20 mb-6">
              <Sparkles className="w-4 h-4 text-brand-brown" />
              <span className="font-sans text-sm font-semibold text-brand-brown uppercase tracking-wider">Community Event</span>
            </motion.div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark mb-3 leading-[1.1]">
              Carbon Circular <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Live</span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-serif text-lg text-gray-600 italic mb-5">
              The Offline Edition
            </motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="font-sans text-base md:text-lg text-gray-700 leading-relaxed max-w-xl mb-6">
              Let's talk carbon. Out loud. Off the record. An informal hour of what's moving, what's breaking, and what everyone's actually thinking — but not posting about.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex flex-wrap gap-3 mb-6">
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-gray-200 text-xs text-gray-700"><Calendar className="w-3 h-3" /> Jul 17 & Sep 26, 2026</span>
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-gray-200 text-xs text-gray-700"><Clock className="w-3 h-3" /> 1 Hour</span>
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-gray-200 text-xs text-gray-700"><MapPin className="w-3 h-3" /> Blue Tokai, Delhi</span>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
              <a href="https://forms.gle/hRJmfLphALXWYH7p8" target="_blank" rel="noopener noreferrer">
                <Button className="btn-gradient text-white font-sans font-medium px-6 py-5 text-sm rounded-full shadow-lg">Save Me a Seat</Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="font-mono text-xs text-brand-brown uppercase tracking-widest">Format</span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mt-3">No slides. No pitch. <span className="italic text-brand-brown">Just real talk.</span></h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} whileHover={{ y: -5 }} className="bg-brand-surface border border-gray-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-brand-brown/10 flex items-center justify-center mx-auto mb-4">
                  <f.icon className="w-6 h-6 text-brand-brown" />
                </div>
                <h3 className="font-serif text-lg text-brand-dark mb-1">{f.title}</h3>
                <p className="font-sans text-sm text-gray-500">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Venue & Details */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800&q=80" alt="Blue Tokai Coffee" className="w-full h-72 object-cover rounded-2xl shadow-xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="font-mono text-xs text-brand-brown uppercase tracking-widest">Venue</span>
              <h3 className="font-serif text-2xl text-brand-dark mt-2 mb-4">Blue Tokai Coffee Roasters</h3>
              <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4">
                Origins Mezzanine & Ground Floor, 48 Janpath, New Delhi 110001
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-brand-green flex-shrink-0" />
                  <div>
                    <p className="font-sans text-sm font-medium text-brand-dark">Next Dates</p>
                    <p className="font-sans text-sm text-gray-500">July 17, 2026 & September 26, 2026</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-brand-green flex-shrink-0" />
                  <div>
                    <p className="font-sans text-sm font-medium text-brand-dark">Duration</p>
                    <p className="font-sans text-sm text-gray-500">1 hour — small group, honest talk</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Host */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-brand-surface border border-gray-100 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-center">
            <img src="https://customer-assets-v7afamib.emergentagent.net/job_4d73cdde-a968-4e98-9845-6bbefee99d6f/artifacts/outpmrh3_WhatsApp%20Image%202026-06-19%20at%2010.57.35.jpeg" alt="Vidhi Gaur" className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-2 border-gray-100" />
            <div>
              <span className="font-mono text-xs text-brand-brown uppercase tracking-widest">Your Host</span>
              <h3 className="font-serif text-xl text-brand-dark mt-1">Vidhi Gaur</h3>
              <p className="font-sans text-sm text-gray-500 mt-1">She writes Carbon Circular. Co-founder & Director, Carbon at eVerse.AI. Presented by Maple.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 relative overflow-hidden bg-gradient-to-br from-brand-surface via-white to-brand-surface">
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark leading-tight mb-4">Spots are limited. <span className="italic text-brand-brown">On purpose.</span></h2>
            <p className="font-sans text-base text-gray-600 mb-8">Drop a reply or DM to RSVP.</p>
            <a href="https://forms.gle/hRJmfLphALXWYH7p8" target="_blank" rel="noopener noreferrer">
              <Button className="group bg-gradient-to-r from-brand-brown to-brand-brown/80 text-white px-8 py-6 rounded-full text-base font-semibold shadow-xl hover:shadow-brand-brown/30 transition-all">
                Save Me a Seat
              </Button>
            </a>
            <p className="font-sans text-sm text-gray-400 mt-4">info@mapleintegratedsolutions.com</p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default CarbonCircularLivePage;
